import { useCallback, useState } from "react";

import { type GraphQLError } from "graphql";

import { type ShowModal } from "../../camkit/core/hooks";
import { useNotificationApi } from "../../notifications";
import { HostedLensDisclaimerEnum } from "../components/disclaimerOptions";

interface DisclaimerHook {
    selectedHostedLensId: string | null;
    setSelectedHostedLensId: (lensId: string) => void;
    isEnabled: boolean;
    setIsEnabled: (isEnabled: boolean) => void;
    handleErrors: (errors: readonly GraphQLError[] | undefined) => void;
    handleToggle: (isEnabled: boolean, lensId?: string) => void;
}

interface DiscalimerAndAcceptanceProps {
    showDisclaimerModal: ShowModal<HostedLensDisclaimerEnum>;
    option: HostedLensDisclaimerEnum | null;
}

export const useDisclaimerAndAcceptance = ({
    option,
    showDisclaimerModal,
}: DiscalimerAndAcceptanceProps): DisclaimerHook => {
    const { sendCustomError } = useNotificationApi();
    const [selectedHostedLensId, setSelectedHostedLensId] = useState<string | null>(null);
    const [isEnabled, setIsEnabled] = useState(false);

    const determineAction = useCallback((isEnabled: boolean, lensId?: string) => {
        if (lensId) {
            if (isEnabled) {
                return HostedLensDisclaimerEnum.UPDATE_TO_PUBLISHED;
            }
            return HostedLensDisclaimerEnum.UPDATE_TO_DRAFT;
        } else {
            return HostedLensDisclaimerEnum.GENERATE;
        }
    }, []);

    const handleToggle = useCallback(
        (isEnabled: boolean, lensId?: string) => {
            setIsEnabled(isEnabled);
            showDisclaimerModal(determineAction(isEnabled, lensId));
            if (lensId) {
                setSelectedHostedLensId(lensId);
            }
        },
        [determineAction, showDisclaimerModal],
    );

    const handleErrors = useCallback(
        (errors: readonly GraphQLError[] | undefined) => {
            if (errors && errors.length > 0) {
                sendCustomError(`There was an error performing the action: ${option}`, errors[0]?.message);
            }
        },
        [option, sendCustomError],
    );

    return {
        selectedHostedLensId,
        setSelectedHostedLensId,
        isEnabled,
        setIsEnabled,
        handleErrors,
        handleToggle,
    };
};
